import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Button } from "@activecollab/components";
import PropTypes from "prop-types";
import { keyCodes } from "~/utils/key_codes.jsx";
import { SubtaskDatePicker } from "./SubtaskDatePicker.jsx";
import { ProjectMembersAssigneePicker } from "../ProjectMembersAssigneePicker.jsx";

const SubtaskForm = ({
  projectId,
  fakeAssigneeName,
  submitText,
  onSubmit,
  id,
  handleCancel,
  assigneeId,
  hiddenFromClient,
  name,
  dueOn,
  taskDueOn,
  isCompleted,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    assigneeId,
    name,
    dueOn,
  });

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setForm((prevState) => ({
        ...prevState,
        name: "",
        dueOn: undefined,
        assigneeId: 0,
      }));
      id && typeof handleCancel === "function" && handleCancel();
      onSubmit({
        body: form.name,
        assignee_id: form.assigneeId,
        due_on: form.dueOn,
      });
    },
    [form, handleCancel, id, onSubmit]
  );

  const handleInputKeyUp = useCallback(
    (e) => {
      if (e && e.which === keyCodes.escape) {
        e.stopPropagation();
        typeof handleCancel === "function" && handleCancel();
      }
    },
    [handleCancel]
  );

  const handleInputChange = useCallback((e) => {
    if (e) {
      const name = e.target.value;
      setForm((prevState) => ({ ...prevState, name }));
    }
  }, []);

  const handleAssigneeChange = useCallback((id) => {
    setForm((prevState) => ({ ...prevState, assigneeId: id }));
  }, []);

  const handleDateChange = useCallback(({ to }) => {
    setForm((prev) => ({ ...prev, dueOn: to }));
  }, []);

  return (
    <form
      className="tw-flex-col tw-p-2 tw-bg-theme-200 tw-rounded-lg"
      onSubmit={handleSubmit}
    >
      <div className="tw-flex tw-items-center tw-gap-2">
        <Input
          type="text"
          name="name"
          onKeyUp={handleInputKeyUp}
          placeholder={t("Name of the subtask")}
          value={form.name}
          onChange={handleInputChange}
          required
          autoComplete="off"
          autoFocus
          className="tw-h-10 tw-flex-1"
        />
        <SubtaskDatePicker
          dueOn={form.dueOn}
          taskDueOn={taskDueOn}
          isCompleted={isCompleted}
          handleDateChange={handleDateChange}
          changeMode="instant"
          userId={typeof assigneeId === "number" ? form.assigneeId : undefined}
          canModify
        />
        <ProjectMembersAssigneePicker
          projectId={projectId}
          assigneeId={form.assigneeId}
          fakeAssigneeName={fakeAssigneeName}
          onChange={handleAssigneeChange}
          hiddenFromClient={hiddenFromClient}
        />
      </div>
      <div className="tw-flex tw-items-center tw-mt-2">
        <Button
          type="submit"
          variant="primary"
        >
          {submitText ? submitText : t("Add")}
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="tw-ml-2"
          onClick={handleCancel}
        >
          {t("Cancel")}
        </Button>
      </div>
    </form>
  );
};

SubtaskForm.propTypes = {
  onSubmit: PropTypes.func,
  handleCancel: PropTypes.func,
  id: PropTypes.number,
  assigneeId: PropTypes.number,
  name: PropTypes.string,
  fakeAssigneeName: PropTypes.string,
  submitText: PropTypes.string,
  projectId: PropTypes.number,
  hiddenFromClient: PropTypes.bool,
  dueOn: PropTypes.number,
  isCompleted: PropTypes.bool,
  taskDueOn: PropTypes.number,
};

SubtaskForm.defaultProps = {
  onSubmit: () => null,
  handleCancel: () => null,
  assigneeId: 0,
  hiddenFromClient: false,
  id: 0,
  name: "",
  fakeAssigneeName: "",
};

SubtaskForm.displayName = "SubtaskForm";

export default SubtaskForm;
